@if (isNotificationFeatureEnabled() || environmentService.isAdministrationCenter) {
  <button type="button" class="daisy-btn daisy-btn-ghost daisy-btn-circle relative shrink-0" aria-label="Notifications"
    daisyTooltip="Notifications" daisyTooltipPosition="left" (click)="onToggleNotificationsPanelClick()">
    <i class="fa-solid fa-bell fa-lg"></i>
    <!-- The same corner count as the top bar's failed-changes badge, so the two read as one family. -->
    @if (showBadge()) {
      <span class="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-error px-1 text-[0.625rem] font-bold leading-none text-error-content shadow-sm">
        {{ notificationsCountComputed() }}
      </span>
    }
  </button>
}

<daisy-drawer [open]="visible()" (closed)="onHideSidebarClick()" title="Notifications"
  widthCssClass="w-full md:w-6/12 xl:w-4/12">

  <!--
    The panel's two views: the officer's record notifications in five categories, which it opens on, and System,
    which holds what is about the application rather than their records (release notes, and the problems this
    session hit).

    System is a toggle in the header beside the close, so it reads as a different LEVEL from the category strip.
    The strip stays on screen while System shows, with no category selected, so the toggle pressed again or any
    category returns to the records. The toggle's count appears only when something is unread. The administration
    center has only System, so it opens there and shows neither the toggle nor the strip.
  -->
  @if (!environmentService.isAdministrationCenter) {
    <button drawerHeaderActions type="button"
      [class]="view() === 'system'
        ? 'daisy-btn daisy-btn-sm gap-2 border-base-content/15 bg-base-300 px-3 text-[13px] font-bold'
        : 'daisy-btn daisy-btn-sm gap-2 border-base-300 bg-base-100 px-3 text-[13px] font-semibold'"
      [attr.aria-pressed]="view() === 'system'"
      [attr.aria-label]="'System, ' + unreadSystemNotificationCountComputed() + ' unread'"
      (click)="onViewChange(view() === 'system' ? 'records' : 'system')">
      <i class="fa-solid fa-gear" [class.text-daisy-primary]="view() === 'system'" aria-hidden="true"></i>
      System
      <!-- Always shown, 0 included, like the category tabs' counts: primary while there is something unread. -->
      <span data-id="system-count"
        [class]="unreadSystemNotificationCountComputed() !== '0'
          ? 'daisy-badge daisy-badge-sm daisy-badge-primary'
          : 'daisy-badge daisy-badge-sm daisy-badge-neutral'">
        {{ unreadSystemNotificationCountComputed() }}
      </span>
    </button>
  }

  @if (!environmentService.isAdministrationCenter) {
    <!-- Both act on whichever view shows: in System, Mark all as read clears only the system notifications. -->
    <div class="mb-2 flex w-full items-center justify-end gap-3">
      <daisy-toggle label="Only show unread" labelCssClass="text-base-content/60"
        [value]="showUnreadOnly()" (valueChange)="showUnreadOnly.set($event)" ariaLabel="Only show unread" />

      @if (view() === 'system' ? showSystemMarkAllAsReadComputed() : showMarkAllAsRead) {
        <span class="h-5 border-l border-base-300" aria-hidden="true"></span>
        <button type="button" class="daisy-btn daisy-btn-link daisy-btn-sm px-0" [disabled]="markAllLoading"
          (click)="onMarkAllAsReadClick()">
          @if (markAllLoading) {
            <span class="daisy-loading daisy-loading-spinner daisy-loading-xs"></span>
          }
          Mark all as read
        </button>
      }
    </div>

    <!--
      Pinned while the list scrolls under it. Five equal columns, each tab its icon and count over its name, so
      every category is always named and the strip never scrolls: side by side, the names and counts need about
      42rem, and this panel is a third to a half of the screen. Stacked, the longest name (Assignments) fits a fifth
      of a phone-width panel at 11px, and the names step up to 12px once the strip is wide enough for them.

      `-mx-2` and a zero `--tab-p` are that fit: the strip borrows 8px of the panel's padding each side, and the tab
      padding daisyUI would put inside each column goes (it also sets the underline's inset, which is why it is the
      variable and not a padding utility).
    -->
    <div role="tablist" aria-label="Category"
      class="daisy-tabs daisy-tabs-border @container sticky top-0 z-10 -mx-2 mb-3 grid grid-cols-5 border-b border-base-300 bg-base-200">
      @for (tab of tabs(); track tab.value) {
        <!-- None is selected while System shows: the header toggle is the selection then. -->
        @let isActive = view() === 'records' && activeTab() === tab.value;
        <button type="button" role="tab" class="daisy-tab h-auto min-w-0 flex-col flex-nowrap gap-1 py-2 [--tab-p:0]"
          [class.daisy-tab-active]="isActive" [attr.aria-selected]="isActive"
          [attr.aria-label]="tab.title + ', ' + tab.count + ' unread'"
          (click)="onTabChange(tab.value)">
          <span class="flex items-center gap-1.5">
            <i [class]="tab.icon + ' opacity-70'" aria-hidden="true"></i>
            <span [attr.data-id]="tab.dataId"
              [class]="tab.count !== '0'
                ? 'daisy-badge daisy-badge-sm daisy-badge-primary'
                : 'daisy-badge daisy-badge-sm daisy-badge-neutral'">
              {{ tab.count }}
            </span>
          </span>
          <span class="max-w-full truncate text-[11px] @min-[28rem]:text-xs" [class.font-bold]="isActive"
            [class.font-medium]="!isActive">{{ tab.title }}</span>
        </button>
      }
    </div>
  }

  @if (view() === 'records') {
    @switch (activeTab()) {
      @case ('0') {
        <!-- reviews -->
        @if (groupedReviewNotifications && !loading) {
          @if (groupedReviewNotifications.length === 0) {
            <ng-container *ngTemplateOutlet="empty; context: { $implicit: 'No review notifications from the last 30 days.' }" />
          } @else {
            @for (n of pageOf(groupedReviewNotifications, reviewPage(), reviewPageSize()); track $index) {
              <lib-notification-feature-incident-notification-template [groupedNotification]="n"></lib-notification-feature-incident-notification-template>
            }
            @if (groupedReviewNotifications.length > 25) {
              <daisy-pagination class="mt-2" [totalItems]="groupedReviewNotifications.length"
                [(page)]="reviewPage" [(pageSize)]="reviewPageSize" [rowsPerPageOptions]="[25, 50, 100]" />
            }
          }
        } @else {
          <ng-container *ngTemplateOutlet="skeleton" />
        }
      }

      @case ('1') {
        <!-- assignments -->
        @if (groupedAssignmentNotifications && !loading) {
          @if (groupedAssignmentNotifications.length === 0) {
            <ng-container *ngTemplateOutlet="empty; context: { $implicit: 'No assignment notifications from the last 30 days.' }" />
          } @else {
            @for (n of pageOf(groupedAssignmentNotifications, assignmentPage(), assignmentPageSize()); track $index) {
              <lib-notification-feature-incident-assignment-notification-template [groupedNotification]="n"></lib-notification-feature-incident-assignment-notification-template>
            }
            @if (groupedAssignmentNotifications.length > 25) {
              <daisy-pagination class="mt-2" [totalItems]="groupedAssignmentNotifications.length"
                [(page)]="assignmentPage" [(pageSize)]="assignmentPageSize" [rowsPerPageOptions]="[25, 50, 100]" />
            }
          }
        } @else {
          <ng-container *ngTemplateOutlet="skeleton" />
        }
      }

      @case ('2') {
        <!-- reports -->
        @if (!loading) {
          @if (reportNotifications.length === 0) {
            <ng-container *ngTemplateOutlet="empty; context: { $implicit: 'No report notifications from the last 30 days.' }" />
          }
          @for (n of reportNotifications; track $index) {
            <lib-notification-feature-link-notification-template [notification]="n" />
          }
        } @else {
          <ng-container *ngTemplateOutlet="skeleton" />
        }
      }

      @case ('3') {
        <!-- alarms -->
        @if (!loading) {
          @if (groupedAlarmNotifications.length === 0) {
            <ng-container *ngTemplateOutlet="empty; context: { $implicit: 'No alarm notifications from the last 30 days.' }" />
          }
          @for (group of groupedAlarmNotifications; track $index) {
            <lib-notification-feature-alarm-notification-template [groupedNotifications]="group"></lib-notification-feature-alarm-notification-template>
          }
        } @else {
          <ng-container *ngTemplateOutlet="skeleton" />
        }
      }

      @case ('4') {
        <!-- integrations -->
        @if (!loading) {
          @if (integrationNotifications.length === 0) {
            <ng-container *ngTemplateOutlet="empty; context: { $implicit: 'No integration notifications from the last 30 days.' }" />
          }
          @for (n of integrationNotifications; track $index) {
            <lib-feature-integration-notification-template [notification]="n" />
          }
        } @else {
          <ng-container *ngTemplateOutlet="skeleton" />
        }
      }
    }
  } @else {
    <!-- system -->
    @if (!hasVisibleSystemNotificationsComputed()) {
      <ng-container *ngTemplateOutlet="empty; context: { $implicit: showUnreadOnly() ? 'No unread system notifications.' : 'No system notifications for the current session.' }" />
    } @else {
      <lib-feature-system-notification-template [unreadOnly]="showUnreadOnly()" />
    }
  }
</daisy-drawer>

<ng-template #empty let-message>
  <div class="flex flex-col items-center justify-between py-6 text-base-content/60">
    <i class="fa-regular fa-clock mb-2 text-6xl"></i>
    <p>{{ message }}</p>
  </div>
</ng-template>

<ng-template #skeleton>
  @for (item of skeletonListItems; track $index) {
    <div class="mt-2 flex gap-2">
      <div class="daisy-skeleton size-16 shrink-0 rounded-full"></div>
      <div class="daisy-skeleton h-16 flex-1"></div>
    </div>
  }
</ng-template>
